import Bugsnag from "@bugsnag/js";
import { get } from "underscore";

// A wrapper around bugsnag.
//
// If you want to track errors on a particular page, you should
// import this module and call ErrorTrack.start() during the page
// object initialization.
//
// Calling ErrorTrack.start() will automatically capture a lot of
// interesting stuff.
//
// See https://docs.bugsnag.com/platforms/browsers/js/
//
export default class ErrorTrack {

  static start(config) {
    try {
      Bugsnag.start({ onError: ErrorTrack.onError, ...config });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Bugsnag failed to start. Bad configuration?");
    }
  }

  // Wraps a call to pass `exception` to Bugsnag
  //
  // `severity` overrides the default severity level. It can be one of "error",
  // "warning" or "info". Default is "warning" for errors passed to `Bugsnag.notify`.
  // See https://docs.bugsnag.com/platforms/javascript/react/customizing-error-reports/#severity
  static notify(exception, severity) {
    if (Bugsnag) {
      Bugsnag.notify(exception, ErrorTrack.notifyCallback({ severity }));
    } else {
      // eslint-disable-next-line no-console
      console.warn("Bugsnag client not initialized! Call ErrorTrack.start() first.");
    }
  }

  // Returns a callback we can pass in to `notify` so as to customize the error
  // report.
  // See https://docs.bugsnag.com/platforms/javascript/react/customizing-error-reports/#the-event-object
  static notifyCallback({ severity }) {
    return (event) => {
      if (severity) {
        // eslint-disable-next-line no-param-reassign
        event.severity = severity;
      }
    };
  }

  // Add any logic here for updating an error event before passing it to Bugsnag.
  // See
  // https://docs.bugsnag.com/platforms/javascript/restify/customizing-error-reports/#updating-events-using-callbacks
  static onError(event) {
    // Open up the Bugsnag Event object.
    // See https://docs.bugsnag.com/platforms/javascript/restify/customizing-error-reports/#the-event-object
    // n.b. we're using underscore's `get` method to provide safe navigation
    // - https://underscorejs.org/#get
    const file = get(event, ["errors", 0, "stacktrace", 0, "file"]);
    // If the stacktrace on the originating error is from a file which matches
    // certain criteria, ignore the event by returning false
    if (file) {
      // If `file` has the `moz-extension` protocol - the error
      // originates from a Firefox extension and it's out of our control to fix
      if (/^moz-extension/.test(file)) {
        return false;
      }
      // Ignore errors from Hypothesis, which uses extensions to mark up
      // existing pages - https://web.hypothes.is/about/
      if (/^https:\/\/via\.hypothes\.is/.test(file)) {
        return false;
      }
      // Ignore errors from the file:// protocol, which indicates that the site
      // is being run from a local file system. Probably this means a user has
      // saved the page and is attempting to open the saved page, which will
      // break payments JS, etc.
      if (/^file:\//.test(file)) {
        return false;
      }
    }
    const { url } = event.request;
    if (url) {
      // Sometimes Bugsnag doesn't report a file, but it should report a URL.
      // As with files with the file:// protocol above, we should ignore reports
      // where the URL indicates the user is using a downloaded version of the
      // app.
      if (/^file:\//.test(url)) {
        return false;
      }
    }
    return true;
  }
}
